import { useEffect, useMemo, useState } from "react";
import { AzureResponseProps, ResourceRequestsProps } from "../resourceRequestInterface";
import { myLocalStorage } from "../../../StorageHelper";
import useUserStore from "../../../../services/userStore";
import {
  AlertTriangle,
  CheckCircle2,
  CheckCircle2Icon,
  Clock,
  Cloud,
  Eye,
  MessageSquare,
  RotateCcw,
  User,
  XCircle,
} from "lucide-react";
import { changeAzureStatus, getAzureNotificationAPI, getAzureNotificationCountAPI, revokeRequest } from "../Api/resourcerequestapi";
import { format } from "date-fns";
import { Card, CardContent } from "../../../ui/card";
import { Badge } from "../../../ui/badge";
import { getStatusColor } from "../utils";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import { formatDate } from "../../formatTimeAgo";

export const AzureUserRequestDetails = ({
  computerName = "",
  searchTerm = "",
  statusFilter = "",
  startDate,
  endDate,
  activeTab
}: ResourceRequestsProps) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<AzureResponseProps[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<AzureResponseProps | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const tenantName = useMemo(
    () => myLocalStorage.getItem("latestTenant").tenantName || "",
    []
  );

  const { email, role } = useUserStore((state) => state.user);
  const [loadingId, setLoadingId] = useState<number | null>(null);


  const handleRequest = async (
    status: string,
    id: number,
    notification: AzureResponseProps
  ) => {
    setLoadingId(id);
    try {
      await changeAzureStatus(status, id, tenantName);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingId(null);
    }

    getAzureNotification(false)
  };

  const getAzureNotificationCount = async () => {
    const response = await getAzureNotificationCountAPI(
      computerName,
      searchTerm,
      statusFilter.toUpperCase(),
      startDate ? format(startDate, "yyyy-MM-dd") : "",
      endDate ? format(endDate, "yyyy-MM-dd") : "",
      tenantName,
      page,
      email,
      role
    )
    setTotalItems(response || 0);
  }

  const getAzureNotification = async (isFirst = true) => {
    setIsLoading(isFirst);
    try {
      const response: AzureResponseProps[] = await getAzureNotificationAPI(
        computerName,
        searchTerm,
        statusFilter.toUpperCase(),
        startDate ? format(startDate, "yyyy-MM-dd") : "",
        endDate ? format(endDate, "yyyy-MM-dd") : "",
        tenantName,
        page - 1,
        email,
        role
      );
      setNotifications(response);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (request: AzureResponseProps) => {
    setSelectedRequest(request);
    setIsDetailsOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Clock className="w-4 h-4" />;
      case "APPROVED":
        return <CheckCircle2Icon className="w-4 h-4" />;
      case "DECLINED":
        return <XCircle className="w-4 h-4" />;
      case "REVOKED":
        return <RotateCcw className="w-4 h-4" />;
      case "EXPIRED":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleRevoke = async (accessId: number) => {
    await revokeRequest("azure", accessId)
    getAzureNotification(false);
  }

  useEffect(() => {
    getAzureNotificationCount();
    getAzureNotification();
    if (activeTab === "azure") {
      const interval = setInterval(() => {
        getAzureNotificationCount();
        getAzureNotification(false);
      }, 2000)
      return () => clearInterval(interval)
    }
  }, [
    computerName,
    searchTerm,
    statusFilter,
    startDate,
    endDate,
    tenantName,
    page,
    email,
    role,
    activeTab
  ]);
  const totalPages = Math.ceil(totalItems / 15);

  return (
    <>
      <div className="space-y-1">
        {isLoading ? (
          <>
            {[...Array(3)].map((_, idx) => (
              <Card key={idx} className="border-slate-200 dark:border-slate-700">
                <CardContent className="p-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <Skeleton className="w-40 h-4" />
                    <Skeleton className="w-12 h-4 ml-auto" />
                  </div>
                  <Skeleton className="w-64 h-3" />
                  <Skeleton className="w-32 h-3" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Skeleton className="w-full h-3" />
                    <Skeleton className="w-full h-3" />
                    <Skeleton className="w-full h-3" />
                    <Skeleton className="w-full h-3" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : notifications.length > 0 ? (
          <>
            {notifications.map((request) => (
              <Card
                key={request.id}
                className="transition-all duration-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover-elevate cursor-pointer transition-transform hover:scale-[1.01]"
                data-testid={`azure-request-card-${request.id}`}
              >
                <CardContent className="p-2">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Cloud className="w-3.5 h-3.5 text-blue-500" />
                              <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                {request.resourceName}
                              </h3>
                            </div>
                            <Badge
                              className={`border text-xs px-1.5 py-0.5 ${getStatusColor(
                                request.status
                              )}`}
                            >
                              <div className="flex items-center gap-1">
                                {getStatusIcon(request.status)}
                                {request.status}
                              </div>
                            </Badge>
                          </div>
                          <p className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <User className="w-4 h-4" />
                            {request.userEmail}
                            <span className="text-slate-400">•</span>
                            {formatDate(request.requestedAt)}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(request)}
                          className="h-auto px-2 py-1 text-xs"
                          data-testid={`button-details-azure-${request.id}`}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Details
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Azure Username
                          </label>
                          <p className="font-medium text-slate-700 dark:text-slate-200">
                            {request.assigneeName}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Role Name
                          </label>
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.roleName}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Resource Group
                          </label>
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.resourceName}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-slate-500 dark:text-slate-400">
                            Duration
                          </label>
                          <p className="text-slate-700 dark:text-slate-200">
                            {request.privilegeLevel === "PERMANENT" ? "Permanent" : `${request.expiryTimeAmount} min`}

                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 p-3 text-sm border border-blue-200 rounded-lg md:grid-cols-3 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
                        <div>
                          <label className="font-medium text-blue-600 dark:text-blue-400">
                            Azure ID
                          </label>
                          <p className="font-mono text-xs text-blue-800 dark:text-blue-200">
                            {request.azureId}
                          </p>
                        </div>
                        <div>
                          <label className="font-medium text-blue-600 dark:text-blue-400">
                            Resource Name
                          </label>
                          <p className="text-xs text-blue-800 dark:text-blue-200">
                            {request.resourceName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <MessageSquare className="w-3 h-3" />
                          {request.description}
                        </div>
                        <div className="flex items-center gap-2">
                          {request.status === "PENDING" && (


                            <>
                              {role !== "TENANT_USER" ? (
                                <>
                                  <Button
                                    disabled={loadingId === request.id}
                                    size="sm"
                                    className="text-white bg-green-600 hover:bg-green-700"
                                    onClick={() => handleRequest("APPROVED", request.id, request)}
                                  >
                                    {loadingId === request.id ? (
                                      <>
                                        <svg
                                          className="animate-spin w-4 h-4 mr-1 text-white"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                          ></path>
                                        </svg>
                                        Approving...
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleRequest("DECLINE", request.id, request)}>

                                    <XCircle className="w-4 h-4 mr-1" /> Decline
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                                    onClick={() => setIsDetailsOpen?.(false)}
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                                  </Button>
                                  {/**
                                     * <Button size="sm" variant="outline" className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                                    onClick={() => { handleRevoke(request.id) }}>
                                    <RotateCcw className="w-4 h-4 mr-1" /> {role === "TENANT_USER" ? "Cancel Request" : "Revoke"}
                                  </Button>
                                     */}

                                </>
                              )}
                            </>

                          )}

                          {false && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-purple-600 border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-950"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="p-12 text-center">
              <Cloud className="w-12 h-12 mx-auto mb-4 text-blue-300 dark:text-blue-600" />
              <h3 className="mb-2 text-lg font-medium text-slate-600 dark:text-slate-300">
                No Azure requests found
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No access requests found for Azure resources
              </p>
            </CardContent>
          </Card>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Showing {(page - 1) * 15 + 1}–{Math.min(page * 15, totalItems)} of {totalItems}
            </div>

            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                data-testid="button-prev-page-onprem"
              >
                Previous
              </Button>

              {/* Dynamic Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                const pageNum = startPage + i;

                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className={pageNum === page ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}
                    data-testid={`page-onprem-${pageNum}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              {/* Next Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                data-testid="button-next-page-onprem"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* ✅ Request Details Dialog */}
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Cloud className="w-5 h-5 text-blue-500" />
                Azure Access Request Details
              </DialogTitle>
              <DialogDescription>
                Complete information for Azure request #{selectedRequest?.id}
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-6 overflow-y-auto max-h-96">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Request ID
                    </label>
                    <p className="font-mono text-slate-700 dark:text-slate-200">
                      {selectedRequest.id}
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Status
                    </label>
                    <Badge className={`${getStatusColor(selectedRequest.status)} mt-1`}>
                      {selectedRequest.status}
                    </Badge>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Requested By
                    </label>
                    <p className="text-slate-700 dark:text-slate-200">
                      {selectedRequest.userEmail}
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Azure Username
                    </label>
                    <p className="text-slate-700 dark:text-slate-200">
                      {selectedRequest.assigneeName}
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Scope
                    </label>
                    <p className="font-mono text-xs text-slate-700 dark:text-slate-200">
                      {selectedRequest.scope}
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Azure ID
                    </label>
                    <p className="font-mono text-xs text-slate-700 dark:text-slate-200">
                      {selectedRequest.azureId}
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Resource Name
                    </label>
                    <p className="text-slate-700 dark:text-slate-200">
                      {selectedRequest.resourceName}
                    </p>
                  </div>
                  <div>
                    <label className="font-medium text-slate-500 dark:text-slate-400">
                      Role Name
                    </label>
                    <p className="text-slate-700 dark:text-slate-200">
                      {selectedRequest.roleName}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="font-medium text-slate-500 dark:text-slate-400">
                    Business Justification
                  </label>
                  <p className="p-3 mt-1 rounded-lg text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800">
                    {selectedRequest.description}
                  </p>
                </div>

                {selectedRequest.status === "PENDING" && (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>
                      Close
                    </Button>
                    {role === "TENANT_USER" ? <Button
                      size="sm"
                      className="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                      onClick={() => setIsDetailsOpen?.(false)}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Pending
                    </Button> : <>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setIsDetailsOpen(false);
                          handleRequest("DECLINE", selectedRequest.id, selectedRequest)
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Decline
                      </Button>
                      <Button
                        onClick={() => {
                          setIsDetailsOpen(false);
                          handleRequest("APPROVED", selectedRequest.id, selectedRequest)
                        }}

                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Approve
                      </Button>

                    </>}

                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};